import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  debounceTime,
  exhaustMap,
  share,
  switchMap,
} from 'rxjs';
import { MonitoringActions } from '@states/monitoring/monitoring.action-types';
import { MonitoringService } from '../../services/monitoring.service';
import { UtilsService } from '../../edge/utils.service';
import { TranslateService } from '@ngx-translate/core';
import { SharedActions } from '@states/shared/shared.action-types';
import { withLatestFrom } from 'rxjs/operators';
import { MonitoringSelectors } from '@states/monitoring/monitoring.selector-types';
import { Store, select } from '@ngrx/store';
import {
  ActionType,
  AlertCategory,
  AlertV2SelectedFlow,
  EventModels,
  IntegrationsType,
  StatusType,
} from '@models/alerts-v2.model';
import { IntegrationsModel } from '@models/integrations.model';
import { AlertsV2Effect } from './alerts-v2.effect';

@Injectable()
export class MonitoringEffects {
  public getData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.getMonitoringAccounts),
      switchMap(() => {
        return this.monitoringService.getData().pipe(
          switchMap((response) => {
            return [
              MonitoringActions.getMonitoringAccountsSuccess({ accounts: response }),
              MonitoringActions.setInitialLoading({ initialLoading: false }),
            ];
          }),
          catchError(() => {
            return [
              MonitoringActions.getMonitoringAccountsFail(),
              MonitoringActions.setInitialLoading({ initialLoading: false }),
            ];
          }),
        );
      }),
      share(),
    ),
  );

  public createAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.createAccount),
      switchMap(({ accountInfo }) => {
        return [
          MonitoringActions.setIsCreatingLoader({ isCreating: true }),
          MonitoringActions.createAccountRequest({ accountInfo }),
        ];
      }),
    ),
  );

  public createAccountRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.createAccountRequest),
      switchMap(({ accountInfo }) => {
        return this.monitoringService.createAccount(accountInfo).pipe(
          switchMap((response) => {
            return [
              SharedActions.showMessage({
                success: this.translateService.instant(
                  'MONITORING_PAGE.ACCOUNT_CREATED_SUCCESS',
                  {
                    entity: response.account.name,
                  },
                ),
              }),
              MonitoringActions.setIsCreatingLoader({ isCreating: false }),
              MonitoringActions.createAccountRequestSuccess({
                account: response.account,
              }),
              MonitoringActions.setAccountContactGroups({
                contactGroups: response.contactGroups,
              }),
            ];
          }),
          catchError(() => {
            return [
              SharedActions.showMessage({
                error: this.translateService.instant(
                  'TOASTS.ENTITY_CREATED_FAIL',
                  {
                    entity: this.translateService.instant(
                      'MONITORING_PAGE.ZONE',
                    ),
                  },
                ),
              }),
              MonitoringActions.setIsCreatingLoader({ isCreating: false }),
              MonitoringActions.createAccountRequestFail(),
            ];
          }),
        );
      }),
      share(),
    ),
  );

  public startCreateContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.startCreateContact),
      switchMap(({ group, groupId, contacts }) => {
        return [
          MonitoringActions.setIsCreatingLoader({ isCreating: true }),
          MonitoringActions.sendCreateContactRequest({ group, groupId, contacts }),
        ];
      }),
    ),
  );

  public sendCreateContactRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.sendCreateContactRequest),
      withLatestFrom(
        this.store$.select(MonitoringSelectors.selectOneAccount),
        this.store$.select(MonitoringSelectors.selectIsCreatingProcess),
      ),
      switchMap(([{ group, groupId, contacts }, account, process]) => {
        const existingContacts = group?.contacts?.map((c) => c.contactId);
        const newContactsIds = contacts?.filter(
          (contact) => !existingContacts?.includes(contact),
        );
        return this.monitoringService
          .createContact(account._id, groupId, newContactsIds)
          .pipe(
            switchMap((response) => {
              const baseActions = [
                MonitoringActions.setIsCreatingLoader({ isCreating: false }),
                MonitoringActions.sendCreateContactRequestSuccess({
                  groupId,
                  contact: response,
                }),
              ];

              return !process
                ? [
                    ...baseActions,
                    SharedActions.showMessage({
                      success: this.translateService.instant(
                        'MONITORING_PAGE.ADD_CONTACTS_SUCCESS',
                      ),
                    }),
                  ]
                : baseActions;
            }),
            catchError(() => {
              return [
                SharedActions.showMessage({
                  error: this.translateService.instant(
                    'TOASTS.ENTITY_CREATED_FAIL',
                    {
                      entity: this.translateService.instant(
                        'MONITORING_PAGE.CONTACT',
                      ),
                    },
                  ),
                }),
                MonitoringActions.setIsCreatingLoader({ isCreating: false }),
                MonitoringActions.sendCreateContactRequestFail(),
              ];
            }),
          );
      }),
      share(),
    ),
  );

  public startCreateContactGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.startCreateContactGroup),
      switchMap(({ groupInfo }) => {
        return [
          MonitoringActions.setIsCreatingLoader({ isCreating: true }),
          MonitoringActions.sendCreateContactGroupRequest({ groupInfo }),
        ];
      }),
    ),
  );

  public sendCreateContactGroupRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.sendCreateContactGroupRequest),
      withLatestFrom(
        this.store$.select(MonitoringSelectors.selectIsCreatingProcess),
      ),
      switchMap(([{ groupInfo }, process]) => {
        return this.monitoringService.createContactGroup(groupInfo).pipe(
          switchMap((response) => {
            const baseActions = [
              MonitoringActions.setIsCreatingLoader({ isCreating: false }),
              MonitoringActions.createContactGroupSuccess({
                group: response,
              }),
            ];

            return !process
              ? [
                  ...baseActions,
                  SharedActions.showMessage({
                    success: this.translateService.instant(
                      'TOASTS.ENTITY_CREATED_SUCCESS',
                      {
                        entity: this.translateService.instant(
                          'MONITORING_PAGE.GROUP',
                        ),
                      },
                    ),
                  }),
                ]
              : baseActions;
          }),
          catchError(() => {
            return [
              SharedActions.showMessage({
                error: this.translateService.instant(
                  'TOASTS.ENTITY_CREATED_FAIL',
                  {
                    entity: this.translateService.instant(
                      'MONITORING_PAGE.GROUP',
                    ),
                  },
                ),
              }),
              MonitoringActions.setIsCreatingLoader({ isCreating: false }),
            ];
          }),
        );
      }),
      share(),
    ),
  );

  public getCurrentAccountPartitions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.getCurrentAccountPartitions),
      switchMap(({ accountId }) => {
        return [
          MonitoringActions.setIsLoading({ isLoading: true }),
          MonitoringActions.getCurrentAccountPartitionsRequest({ accountId }),
        ];
      }),
    ),
  );

  public getCurrentAccountPartitionsRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.getCurrentAccountPartitionsRequest),
      switchMap(({ accountId }) => {
        return this.monitoringService.getPartitions(accountId).pipe(
          switchMap((response) => {
            return [
              MonitoringActions.setIsLoading({ isLoading: false }),
              MonitoringActions.setPartitions({ partitions: response }),
            ];
          }),
          catchError(() => {
            return [MonitoringActions.setIsLoading({ isLoading: false })];
          }),
        );
      }),
      share(),
    ),
  );

  public getCurrentPartition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.getCurrentPartition),
      switchMap(({ partitionId }) => {
        return [
          MonitoringActions.getCurrentPartitionRequest({ partitionId }),
        ];
      }),
    ),
  );

  public getCurrentPartitionRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.getCurrentPartitionRequest),
      switchMap(({ partitionId }) => {
        return this.monitoringService.getPartition(partitionId).pipe(
          switchMap((response) => {
            return [
              MonitoringActions.getCurrentPartitionSuccess({
                partition: response,
              }),
            ];
          }),
          catchError(() => {
            return [];
          }),
        );
      }),
      share(),
    ),
  );

  public startSavePartitionSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.startSavePartitionSchedule),
      switchMap(({ partitionId, accountSchedule }) => {
        return [
          MonitoringActions.setIsUpdating({ isUpdating: true }),
          MonitoringActions.savePartitionScheduleRequest({
            partitionId,
            accountSchedule,
          }),
        ];
      }),
    ),
  );

  public savePartitionScheduleRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.savePartitionScheduleRequest),
      withLatestFrom(this.store$.select(MonitoringSelectors.selectOneAccount)),
      switchMap(([{ partitionId, accountSchedule }, account]) => {
        return this.monitoringService
          .updatePartitionSchedule(account._id, partitionId, accountSchedule)
          .pipe(
            switchMap((response) => {
              return [
                SharedActions.showMessage({
                  success: this.translateService.instant(
                    'TOASTS.ENTITY_UPDATE_SUCCESS',
                    {
                      entity: this.translateService.instant(
                        'MONITORING_PAGE.SCHEDULE_TITLE',
                      ),
                    },
                  ),
                }),
                MonitoringActions.savePartitionScheduleSuccess({
                  accountSchedule: response,
                }),
                MonitoringActions.setIsUpdating({ isUpdating: false }),
              ];
            }),
            catchError(() => {
              return [
                SharedActions.showMessage({
                  error: this.translateService.instant(
                    'TOASTS.ENTITY_UPDATE_FAILED',
                    {
                      entity: this.translateService.instant(
                        'MONITORING_PAGE.SCHEDULE_TITLE',
                      ),
                    },
                  ),
                }),
                MonitoringActions.setIsUpdating({ isUpdating: false }),
              ];
            }),
          );
      }),
      share(),
    ),
  );

  public getPartitionSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.getPartitionSchedule),
      switchMap(({ partitionId }) => {
        return [
          MonitoringActions.setIsPartitionScheduleLoading({
            isPartitionScheduleLoading: true,
          }),
          MonitoringActions.getPartitionScheduleRequest({ partitionId }),
        ];
      }),
    ),
  );

  public getPartitionScheduleRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.getPartitionScheduleRequest),
      switchMap(({ partitionId }) => {
        return this.monitoringService.getPartitionSchedule(partitionId).pipe(
          switchMap((response) => {
            return [
              MonitoringActions.setPartitionSchedule({
                partitionSchedule: response,
              }),
              MonitoringActions.setIsPartitionScheduleLoading({
                isPartitionScheduleLoading: false,
              }),
            ];
          }),
          catchError(() => {
            return [
              MonitoringActions.setIsPartitionScheduleLoading({
                isPartitionScheduleLoading: false,
              }),
            ];
          }),
        );
      }),
      share(),
    ),
  );

  public startUpdateAccountSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.startUpdateAccountSchedule),
      switchMap(({ accountSchedule }) => {
        return [
          MonitoringActions.setIsUpdating({ isUpdating: true }),
          MonitoringActions.setIsCreatingLoader({ isCreating: true }),
          MonitoringActions.updateAccountSchedule({
            accountSchedule,
          }),
        ];
      }),
    ),
  );

  public updateAccountSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.updateAccountSchedule),
      withLatestFrom(this.store$.select(MonitoringSelectors.selectOneAccount)),
      switchMap(([{ accountSchedule }, account]) => {
        return this.monitoringService
          .updateAccountSchedule(account._id, accountSchedule)
          .pipe(
            switchMap((response) => {
              return [
                SharedActions.showMessage({
                  success: this.translateService.instant(
                    'TOASTS.ENTITY_UPDATE_SUCCESS',
                    {
                      entity: this.translateService.instant(
                        'MONITORING_PAGE.SCHEDULE_TITLE',
                      ),
                    },
                  ),
                }),
                MonitoringActions.updateAccountScheduleSuccess({
                  accountSchedule: response,
                }),
                MonitoringActions.setIsUpdating({ isUpdating: false }),
                MonitoringActions.setIsCreatingLoader({ isCreating: false }),
              ];
            }),
            catchError(() => {
              return [
                MonitoringActions.setIsLoading({ isLoading: false }),
                MonitoringActions.setIsCreatingLoader({ isCreating: false }),
              ];
            }),
          );
      }),
      share(),
    ),
  );

  public startUpdateContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.startUpdateContact),
      switchMap(({ id, contactInfo }) => {
        return [
          MonitoringActions.setIsCreatingLoader({ isCreating: true }),
          MonitoringActions.sendUpdateContactRequest({ id, contactInfo }),
        ];
      }),
    ),
  );

  public sendUpdateContactRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.sendUpdateContactRequest),
      withLatestFrom(this.store$.select(MonitoringSelectors.selectOneAccount)),
      switchMap(([{ id, contactInfo }, account]) => {
        return this.monitoringService
          .updateContact(String(id), contactInfo)
          .pipe(
            switchMap(() => {
              return [
                SharedActions.showMessage({
                  success: this.translateService.instant(
                    'TOASTS.ENTITY_UPDATE_SUCCESS',
                    {
                      entity: this.translateService.instant(
                        'MONITORING_PAGE.CONTACT',
                      ),
                    },
                  ),
                }),
                MonitoringActions.getMonitoringContacts({
                  id: account._id,
                }),
                MonitoringActions.setIsCreatingLoader({ isCreating: false }),
                MonitoringActions.sendUpdateContactRequestSuccess(),
              ];
            }),
            catchError(() => {
              return [
                SharedActions.showMessage({
                  error: this.translateService.instant(
                    'TOASTS.ENTITY_UPDATE_FAILED',
                    {
                      entity: this.translateService.instant(
                        'MONITORING_PAGE.CONTACT',
                      ),
                    },
                  ),
                }),
                MonitoringActions.setIsCreatingLoader({ isCreating: false }),
                MonitoringActions.sendUpdateContactRequestFail(),
              ];
            }),
          );
      }),
      share(),
    ),
  );

  public startUpdatePartition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.startUpdatePartition),
      switchMap(({ partitionId, partitionDetails }) => {
        return [
          MonitoringActions.setIsUpdating({ isUpdating: true }),
          MonitoringActions.sendUpdatePartitionRequest({
            partitionId,
            partitionDetails,
          }),
        ];
      }),
    ),
  );

  public sendUpdatePartitionRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.sendUpdatePartitionRequest),
      switchMap(({ partitionId, partitionDetails }) => {
        return this.monitoringService
          .updatePartition(partitionId, partitionDetails)
          .pipe(
            switchMap(() => {
              return [
                SharedActions.showMessage({
                  success: this.translateService.instant(
                    'TOASTS.ENTITY_UPDATE_SUCCESS',
                    {
                      entity: this.translateService.instant(
                        'MONITORING_PAGE.PARTITION',
                      ),
                    },
                  ),
                }),
                MonitoringActions.setIsUpdating({ isUpdating: false }),
                MonitoringActions.sendUpdatePartitionRequestSuccess({
                  partitionDetails,
                }),
              ];
            }),
            catchError(() => {
              return [
                SharedActions.showMessage({
                  error: this.translateService.instant(
                    'TOASTS.ENTITY_UPDATE_FAILED',
                    {
                      entity: this.translateService.instant(
                        'MONITORING_PAGE.PARTITION',
                      ),
                    },
                  ),
                }),
                MonitoringActions.setIsUpdating({ isUpdating: false }),
                MonitoringActions.sendUpdatePartitionRequestFail(),
              ];
            }),
          );
      }),
      share(),
    ),
  );

  public getPartitionZones$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.getPartitionZones),
      switchMap(({ accountId, partitionId }) => {
        return [
          MonitoringActions.setIsZonesLoading({ isZonesLoading: true }),
          MonitoringActions.getPartitionZonesRequest({
            accountId,
            partitionId,
          }),
        ];
      }),
    ),
  );

  public getPartitionZonesRequest = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.getPartitionZonesRequest),
      switchMap(({ accountId, partitionId }) => {
        return this.monitoringService
          .getPartitionZones(accountId, partitionId)
          .pipe(
            switchMap((zones) => {
              return [
                MonitoringActions.setSelectedZones({ zones }),
                MonitoringActions.setIsZonesLoading({ isZonesLoading: false }),
              ];
            }),
            catchError(() => {
              return [
                MonitoringActions.setIsZonesLoading({ isZonesLoading: false }),
              ];
            }),
          );
      }),
    ),
  );

  public getAllOrganizationContacts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.getAllOrganizationContacts),
      switchMap(() => {
        return [MonitoringActions.getAllOrganizationContactsRequest()];
      }),
    ),
  );

  public getAllOrganizationContactsRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.getAllOrganizationContactsRequest),
      switchMap(() => {
        return this.monitoringService.getAllOrganizationContacts().pipe(
          switchMap((contacts) => {
            return [
              MonitoringActions.getAllOrganizationContactsSuccess({
                allOrganizationContacts: contacts,
              }),
            ];
          }),
          catchError((response) => {
            return [
              SharedActions.showMessage({
                error: this.utilsService.errMessage(response),
              }),
              MonitoringActions.getAllOrganizationContactsFail(),
            ];
          }),
        );
      }),
    ),
  );

  public startCreateOrgContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.startCreateOrgContact),
      switchMap(({ contactInfo }) => {
        return [
          MonitoringActions.setIsCreatingLoader({ isCreating: true }),
          MonitoringActions.startCreateOrgContactRequest({ contactInfo }),
        ];
      }),
    ),
  );

  public startCreateOrgContactRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.startCreateOrgContactRequest),
      switchMap(({ contactInfo }) => {
        return this.monitoringService.createOrgContact(contactInfo).pipe(
          switchMap((response) => {
            return [
              SharedActions.showMessage({
                success: this.translateService.instant(
                  'TOASTS.ENTITY_CREATED_SUCCESS',
                  {
                    entity: this.translateService.instant(
                      'MONITORING_PAGE.CONTACT',
                    ),
                  },
                ),
              }),
              MonitoringActions.setIsCreatingLoader({ isCreating: false }),
              MonitoringActions.startCreateOrgContactSuccess({
                contact: response,
              }),
            ];
          }),
          catchError(() => {
            return [
              SharedActions.showMessage({
                error: this.translateService.instant(
                  'TOASTS.ENTITY_CREATED_FAIL',
                  {
                    entity: this.translateService.instant(
                      'MONITORING_PAGE.CONTACT',
                    ),
                  },
                ),
              }),
              MonitoringActions.setIsCreatingLoader({ isCreating: false }),
            ];
          }),
        );
      }),
      share(),
    ),
  );

  public startCreateFirstOrgContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.startCreateFirstOrgContact),
      switchMap(({ contactInfo }) => {
        return [
          MonitoringActions.setIsCreatingLoader({ isCreating: true }),
          MonitoringActions.startCreateFirstOrgContactRequest({ contactInfo }),
        ];
      }),
    ),
  );

  public startCreateFirstOrgContactRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.startCreateFirstOrgContactRequest),
      withLatestFrom(this.store$.select(MonitoringSelectors.selectAccountGroups)),
      switchMap(([{ contactInfo }, groups]) => {
        return this.monitoringService.createOrgContact(contactInfo).pipe(
          switchMap((response) => {
            return [
              SharedActions.showMessage({
                success: this.translateService.instant(
                  'TOASTS.ENTITY_CREATED_SUCCESS',
                  {
                    entity: this.translateService.instant(
                      'MONITORING_PAGE.CONTACT',
                    ),
                  },
                ),
              }),
              MonitoringActions.setIsCreatingLoader({ isCreating: false }),
              MonitoringActions.startCreateOrgContactSuccess({
                contact: response,
              }),
              MonitoringActions.startCreateContact({
                group: groups[0],
                groupId: groups[0]._id,
                contacts: [response._id],
              }),
            ];
          }),
          catchError(() => {
            return [
              SharedActions.showMessage({
                error: this.translateService.instant(
                  'TOASTS.ENTITY_CREATED_FAIL',
                  {
                    entity: this.translateService.instant(
                      'MONITORING_PAGE.CONTACT',
                    ),
                  },
                ),
              }),
              MonitoringActions.setIsCreatingLoader({ isCreating: false }),
            ];
          }),
        );
      }),
      share(),
    ),
  );

  public startUpdateOrgContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.startUpdateOrgContact),
      switchMap(({ contactId, contactInfo, vacation }) => {
        return [
          MonitoringActions.setIsLoading({ isLoading: true }),
          MonitoringActions.startUpdateOrgContactRequest({
            contactId,
            contactInfo,
            vacation,
          }),
        ];
      }),
    ),
  );

  public startUpdateOrgContactRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.startUpdateOrgContactRequest),
      switchMap(({ contactId, contactInfo, vacation }) => {
        return this.monitoringService
          .updateOrgContact(contactId, contactInfo, vacation)
          .pipe(
            switchMap(() => {
              return [
                SharedActions.showMessage({
                  success: this.translateService.instant(
                    'TOASTS.ENTITY_UPDATE_SUCCESS',
                    {
                      entity: this.translateService.instant(
                        'MONITORING_PAGE.CONTACT',
                      ),
                    },
                  ),
                }),
                MonitoringActions.setIsLoading({ isLoading: false }),
                MonitoringActions.startUpdateOrgContactSuccess({
                  contactId,
                  contactInfo,
                  vacation,
                }),
              ];
            }),
            catchError(() => {
              return [
                SharedActions.showMessage({
                  error: this.translateService.instant(
                    'TOASTS.ENTITY_UPDATE_FAILED',
                    {
                      entity: this.translateService.instant(
                        'MONITORING_PAGE.CONTACT',
                      ),
                    },
                  ),
                }),
                MonitoringActions.setIsLoading({ isLoading: false }),
              ];
            }),
          );
      }),
      share(),
    ),
  );

  public enableAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.enableAccount),
      switchMap(({ accountId }) => {
        return this.monitoringService.enableAccount(accountId).pipe(
          switchMap(() => {
            return [MonitoringActions.setEnable({ accountId })];
          }),
          catchError(() => {
            return [];
          }),
        );
      }),
      share(),
    ),
  );

  public disableAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.disableAccount),
      switchMap(({ accountId }) => {
        return this.monitoringService.disableAccount(accountId).pipe(
          switchMap(() => {
            return [MonitoringActions.setDisable({ accountId })];
          }),
          catchError(() => {
            return [];
          }),
        );
      }),
      share(),
    ),
  );

  public enablePartition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.enablePartition),
      switchMap(({ partitionId }) => {
        return this.monitoringService.enablePartition(partitionId).pipe(
          switchMap(() => {
            return [MonitoringActions.enablePartitionSuccess({ partitionId })];
          }),
          catchError(() => {
            return [];
          }),
        );
      }),
      share(),
    ),
  );

  public disablePartition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.disablePartition),
      switchMap(({ partitionId }) => {
        return this.monitoringService.disablePartition(partitionId).pipe(
          switchMap(() => {
            return [MonitoringActions.disablePartitionSuccess({ partitionId })];
          }),
          catchError(() => {
            return [];
          }),
        );
      }),
      share(),
    ),
  );

  public startDeleteOrgContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.startDeleteOrgContact),
      switchMap(({ contactId }) => {
        return [
          MonitoringActions.setIsLoading({ isLoading: true }),
          MonitoringActions.startDeleteOrgContactRequest({ contactId }),
        ];
      }),
    ),
  );

  public startDeleteOrgContactRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.startDeleteOrgContactRequest),
      switchMap(({ contactId }) => {
        return this.monitoringService.deleteOrgContact(contactId).pipe(
          switchMap(() => {
            return [
              SharedActions.showMessage({
                success: this.translateService.instant(
                  'TOASTS.ENTITY_HAS_BEEN_REMOVED',
                  {
                    entity: this.translateService.instant(
                      'MONITORING_PAGE.CONTACT',
                    ),
                  },
                ),
              }),
              MonitoringActions.setIsLoading({ isLoading: false }),
              MonitoringActions.startDeleteOrgContactSuccess({ contactId }),
            ];
          }),
          catchError(() => {
            return [
              SharedActions.showMessage({
                error: this.translateService.instant(
                  'TOASTS.ENTITY_HAS_BEEN_REMOVED_FAILED',
                  {
                    entity: this.translateService.instant(
                      'MONITORING_PAGE.CONTACT',
                    ),
                  },
                ),
              }),
              MonitoringActions.setIsLoading({ isLoading: false }),
            ];
          }),
        );
      }),
      share(),
    ),
  );

  public startSaveZonesByTags$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.startSaveZonesByTags),
      switchMap(({ tags }) => {
        return [
          MonitoringActions.setIsLoading({ isLoading: true }),
          MonitoringActions.startSaveZonesByTagsRequest({ tags }),
        ];
      }),
    ),
  );

  public startSaveZonesByTagsRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.startSaveZonesByTagsRequest),
      withLatestFrom(
        this.store$.select(MonitoringSelectors.selectOneAccount),
        this.store$.select(MonitoringSelectors.selectCurrentPartitionId),
      ),
      switchMap(([{ tags }, account, partitionId]) => {
        return this.monitoringService
          .saveSelectedZones(account._id, String(partitionId), tags)
          .pipe(
            switchMap((response) => {
              return [
                SharedActions.showMessage({
                  success: this.translateService.instant(
                    'MONITORING_PAGE.ZONES_SAVE_SUCCESS',
                  ),
                }),
                MonitoringActions.setIsLoading({ isLoading: false }),
                MonitoringActions.startSaveZonesSuccess({ zones: response }),
              ];
            }),
            catchError((error) => {
              return [
                SharedActions.showMessage({
                  error: error?.error?.message,
                }),
                MonitoringActions.setIsLoading({ isLoading: false }),
              ];
            }),
          );
      }),
      share(),
    ),
  );

  public getAccountSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.getAccountSchedule),
      switchMap(({ accountId }) => {
        return [
          MonitoringActions.setIsScheduleLoading({ isScheduleLoading: true }),
          MonitoringActions.getAccountScheduleRequest({ accountId }),
        ];
      }),
    ),
  );

  public getAccountScheduleRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.getAccountScheduleRequest),
      switchMap(({ accountId }) => {
        return this.monitoringService.getAccountSchedule(accountId).pipe(
          switchMap((response) => {
            return [
              MonitoringActions.setCurrentAccountSchedule({
                currentAccountSchedule: response,
              }),
              MonitoringActions.setIsScheduleLoading({
                isScheduleLoading: false,
              }),
            ];
          }),
          catchError(() => {
            return [
              MonitoringActions.setIsScheduleLoading({
                isScheduleLoading: false,
              }),
            ];
          }),
        );
      }),
      share(),
    ),
  );

  public reorderGroupContacts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.reorderGroupContacts),
      withLatestFrom(this.store$.select(MonitoringSelectors.selectOneAccount)),
      switchMap(([{ orderedContactsIds, groupId }, account]) => {
        return this.monitoringService
          .reorderGroupContacts(account._id, groupId, orderedContactsIds)
          .pipe(
            switchMap(() => {
              return [
                MonitoringActions.reorderGroupContactsSuccess({
                  orderedContactsIds,
                  groupId,
                }),
                SharedActions.showMessage({
                  success: this.translateService.instant(
                    'MONITORING_PAGE.CONTACTS_ORDER',
                  ),
                }),
              ];
            }),
            catchError(() => {
              return [];
            }),
          );
      }),
      share(),
    ),
  );

  public startCreatePartition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.startCreatePartition),
      switchMap(({ partitionInfo }) => {
        return [
          MonitoringActions.setIsCreatingLoader({ isCreating: true }),
          MonitoringActions.sendCreatePartitionRequest({ partitionInfo }),
        ];
      }),
    ),
  );

  public sendCreatePartitionRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.sendCreatePartitionRequest),
      withLatestFrom(this.store$.select(MonitoringSelectors.selectOneAccount)),
      switchMap(([{ partitionInfo }, account]) => {
        return this.monitoringService
          .createPartition(account._id, partitionInfo)
          .pipe(
            switchMap((response) => {
              return [
                SharedActions.showMessage({
                  success: this.translateService.instant(
                    'TOASTS.ENTITY_CREATED_SUCCESS',
                    {
                      entity: this.translateService.instant(
                        'MONITORING_PAGE.PARTITIONS_PAGE.ENTITY',
                      ),
                    },
                  ),
                }),
                MonitoringActions.setIsCreatingLoader({ isCreating: false }),
                MonitoringActions.sendCreatePartitionRequestSuccess({
                  partition: response,
                }),
              ];
            }),
            catchError(() => {
              return [
                SharedActions.showMessage({
                  error: this.translateService.instant(
                    'TOASTS.ENTITY_CREATED_FAIL',
                    {
                      entity: this.translateService.instant(
                        'MONITORING_PAGE.PARTITIONS_PAGE.ENTITY',
                      ),
                    },
                  ),
                }),
                MonitoringActions.setIsCreatingLoader({ isCreating: false }),
                MonitoringActions.sendCreatePartitionRequestFail(),
              ];
            }),
          );
      }),
      share(),
    ),
  );

  public updateAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.updateAccount),
      switchMap(({ id, accountInfo }) => {
        return [
          MonitoringActions.updateAccountRequest({ id, accountInfo }),
          MonitoringActions.setIsUpdating({ isUpdating: true }),
        ];
      }),
    ),
  );

  public updateAccountRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.updateAccountRequest),
      switchMap(({ id, accountInfo }) => {
        return this.monitoringService.updateAccount(id, accountInfo).pipe(
          switchMap(() => {
            return [
              SharedActions.showMessage({
                success: this.translateService.instant(
                  'TOASTS.ENTITY_UPDATE_SUCCESS',
                  {
                    entity: this.translateService.instant(
                      'MONITORING_PAGE.ACCOUNT',
                    ),
                  },
                ),
              }),
              MonitoringActions.setIsUpdating({ isUpdating: false }),
              MonitoringActions.updateAccountRequestSuccess({
                id,
                accountInfo,
              }),
            ];
          }),
          catchError(() => {
            return [
              SharedActions.showMessage({
                error: this.translateService.instant(
                  'TOASTS.ENTITY_UPDATE_FAILED',
                  {
                    entity: this.translateService.instant(
                      'MONITORING_PAGE.ACCOUNT',
                    ),
                  },
                ),
              }),
              MonitoringActions.setIsUpdating({ isUpdating: false }),
              MonitoringActions.updateAccountRequestFail(),
            ];
          }),
        );
      }),
      share(),
    ),
  );

  public getOneAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.getOneAccount),
      switchMap(({ id }) => {
        return this.monitoringService.getOne(id).pipe(
          switchMap((res) => {
            return [MonitoringActions.getOneAccountSuccess({ account: res })];
          }),
          catchError(() => {
            return [MonitoringActions.getOneAccountFail()];
          }),
          share(),
        );
      }),
    ),
  );

  public startDeleteAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.startDeleteAccount),
      debounceTime(400),
      exhaustMap(({ id }) => [
        MonitoringActions.sendDeleteAccountRequest({ id }),
        MonitoringActions.setIsDeleting({ isDeleting: true }),
      ]),
    ),
  );

  public sendDeleteAccountRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.sendDeleteAccountRequest),
      switchMap(({ id }) => {
        return this.monitoringService.deleteAccountById(id).pipe(
          switchMap(() => {
            return [
              SharedActions.showMessage({
                success: this.translateService.instant(
                  'TOASTS.ENTITY_HAS_BEEN_REMOVED',
                  {
                    entity: this.translateService.instant(
                      'MONITORING_PAGE.ACCOUNT',
                    ),
                  },
                ),
              }),
              MonitoringActions.sendDeleteAccountRequestSuccess({ id }),
              MonitoringActions.setIsDeleting({ isDeleting: false }),
            ];
          }),
          catchError(() => {
            return [
              SharedActions.showMessage({
                error: this.translateService.instant(
                  'TOASTS.ENTITY_HAS_BEEN_REMOVED_FAILED',
                  {
                    entity: this.translateService.instant(
                      'MONITORING_PAGE.ACCOUNT',
                    ),
                  },
                ),
              }),
              MonitoringActions.sendDeleteAccountRequestFail({ id }),
              MonitoringActions.setIsDeleting({ isDeleting: false }),
            ];
          }),
          share(),
        );
      }),
    ),
  );

  public startDeleteContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.startDeleteContact),
      switchMap(({ groupId, contactId }) => {
        return this.monitoringService.deleteContact(groupId, contactId).pipe(
          switchMap(() => {
            return [
              SharedActions.showMessage({
                success: this.translateService.instant(
                  'TOASTS.ENTITY_HAS_BEEN_REMOVED',
                  {
                    entity: this.translateService.instant(
                      'MONITORING_PAGE.CONTACT',
                    ),
                  },
                ),
              }),
              MonitoringActions.startDeleteContactSuccess({
                contactId,
                groupId,
              }),
            ];
          }),
          catchError(() => {
            return [
              SharedActions.showMessage({
                error: this.translateService.instant(
                  'TOASTS.ENTITY_HAS_BEEN_REMOVED_FAILED',
                  {
                    entity: this.translateService.instant(
                      'MONITORING_PAGE.CONTACT',
                    ),
                  },
                ),
              }),
            ];
          }),
          share(),
        );
      }),
    ),
  );

  public startDeleteContactGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.startDeleteContactGroup),
      withLatestFrom(
        this.store$.select(MonitoringSelectors.selectOneAccount),
        this.store$.select(MonitoringSelectors.selectIsCreatingProcess),
      ),
      switchMap(([{ groupId }, account, process]) => {
        return this.monitoringService
          .deleteContactGroup(groupId, account._id)
          .pipe(
            switchMap(() => {
              const baseActions = [
                MonitoringActions.startDeleteContactGroupSuccess({ groupId }),
              ];
              return !process
                ? [
                    ...baseActions,
                    SharedActions.showMessage({
                      success: this.translateService.instant(
                        'TOASTS.ENTITY_HAS_BEEN_REMOVED',
                        {
                          entity: this.translateService.instant(
                            'MONITORING_PAGE.GROUP',
                          ),
                        },
                      ),
                    }),
                  ]
                : baseActions;
            }),
            catchError(() => {
              return [
                SharedActions.showMessage({
                  error: this.translateService.instant(
                    'TOASTS.ENTITY_HAS_BEEN_REMOVED_FAILED',
                    {
                      entity: this.translateService.instant(
                        'MONITORING_PAGE.GROUP',
                      ),
                    },
                  ),
                }),
              ];
            }),
            share(),
          );
      }),
    ),
  );

  public startDeletePartition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.startDeletePartition),
      switchMap(({ partitionId }) => [
        MonitoringActions.startDeletePartitionRequest({ partitionId }),
        MonitoringActions.setIsDeleting({ isDeleting: true }),
      ]),
    ),
  );

  public startDeletePartitionRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.startDeletePartitionRequest),
      switchMap(({ partitionId }) => {
        return this.monitoringService.deletePartition(partitionId).pipe(
          switchMap(() => {
            return [
              SharedActions.showMessage({
                success: this.translateService.instant(
                  'TOASTS.ENTITY_HAS_BEEN_REMOVED',
                  {
                    entity: this.translateService.instant(
                      'MONITORING_PAGE.PARTITION',
                    ),
                  },
                ),
              }),
              MonitoringActions.startDeletePartitionSuccess({ partitionId }),
              MonitoringActions.setIsDeleting({ isDeleting: false }),
            ];
          }),
          catchError(() => {
            return [
              SharedActions.showMessage({
                error: this.translateService.instant(
                  'TOASTS.ENTITY_HAS_BEEN_REMOVED_FAILED',
                  {
                    entity: this.translateService.instant(
                      'MONITORING_PAGE.PARTITION',
                    ),
                  },
                ),
              }),
              MonitoringActions.setIsDeleting({ isDeleting: false }),
            ];
          }),
          share(),
        );
      }),
    ),
  );

  public startUpdateGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.startUpdateGroup),
      withLatestFrom(
        this.store$.select(MonitoringSelectors.selectIsCreatingProcess),
      ),
      switchMap(([{ groupId, name }, process]) => {
        return this.monitoringService.updateGroup(groupId, name).pipe(
          switchMap(() => {
            const baseActions = [
              MonitoringActions.updateGroup({ groupId, name }),
            ];
            return !process
              ? [
                  ...baseActions,
                  SharedActions.showMessage({
                    success: this.translateService.instant(
                      'TOASTS.ENTITY_UPDATE_SUCCESS',
                      {
                        entity: this.translateService.instant(
                          'MONITORING_PAGE.GROUP',
                        ),
                      },
                    ),
                  }),
                ]
              : baseActions;
          }),
          catchError(() => {
            return [
              SharedActions.showMessage({
                error: this.translateService.instant(
                  'TOASTS.ENTITY_UPDATE_FAILED',
                  {
                    entity: this.translateService.instant(
                      'MONITORING_PAGE.GROUP',
                    ),
                  },
                ),
              }),
            ];
          }),
          share(),
        );
      }),
    ),
  );

  public getMonitoringContacts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.getMonitoringContacts),
      switchMap(({ id }) => {
        return [
          MonitoringActions.setIsContactsLoading({ isContactsLoading: true }),
          MonitoringActions.sendMonitoringContactsRequest({ id }),
        ];
      }),
    ),
  );

  public sendMonitoringContactsRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.sendMonitoringContactsRequest),
      switchMap(({ id }) => {
        return this.monitoringService.getContacts(id).pipe(
          switchMap((response) => {
            return [
              MonitoringActions.setIsContactsLoading({
                isContactsLoading: false,
              }),
              MonitoringActions.getMonitoringContactsSuccess({
                contacts: response,
              }),
            ];
          }),
          catchError(() => {
            return [
              MonitoringActions.setIsContactsLoading({
                isContactsLoading: false,
              }),
              MonitoringActions.getMonitoringContactsFail(),
            ];
          }),
        );
      }),
      share(),
    ),
  );

  public getMonitoringContactGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.getMonitoringContactGroups),
      switchMap(({ id }) => {
        return [MonitoringActions.sendMonitoringContactGroupsRequest({ id })];
      }),
    ),
  );

  public sendMonitoringContactGroupsRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.sendMonitoringContactGroupsRequest),
      switchMap(({ id }) => {
        return this.monitoringService.getContactGroups(id).pipe(
          switchMap((response) => {
            return [
              MonitoringActions.setAccountContactGroups({
                contactGroups: response,
              }),
            ];
          }),
          catchError(() => {
            return [MonitoringActions.getMonitoringContactGroupsFail()];
          }),
        );
      }),
      share(),
    ),
  );

  public getGrantedOrganizations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.getGrantedOrganizations),
      switchMap(() => {
        return [
          MonitoringActions.setIsLoading({ isLoading: true }),
          MonitoringActions.getGrantedOrganizationsRequest(),
        ];
      }),
    ),
  );

  public getGrantedOrganizationsRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.getGrantedOrganizationsRequest),
      withLatestFrom(this.store$.select(MonitoringSelectors.selectOneAccount)),
      switchMap(([, account]) => {
        return this.monitoringService.getGrantedOrganizations(account._id).pipe(
          switchMap((response) => {
            return [
              MonitoringActions.getGrantedOrganizationsSuccess({
                grantedOrganizations: response,
              }),
              MonitoringActions.setIsLoading({ isLoading: false }),
            ];
          }),
          catchError(() => {
            return [MonitoringActions.setIsLoading({ isLoading: false })];
          }),
        );
      }),
      share(),
    ),
  );

  public createGrantedOrganizations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.createGrantedOrganizations),
      switchMap(({ inviteCode }) => {
        return [
          MonitoringActions.setIsCreatingLoader({ isCreating: true }),
          MonitoringActions.createGrantedOrganizationsRequest({ inviteCode }),
        ];
      }),
    ),
  );

  public createGrantedOrganizationsRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.createGrantedOrganizationsRequest),
      withLatestFrom(this.store$.select(MonitoringSelectors.selectOneAccount)),
      switchMap(([{ inviteCode }, account]) => {
        return this.monitoringService
          .createGrantedOrganization(account._id, inviteCode)
          .pipe(
            switchMap((response) => {
              return [
                MonitoringActions.setIsCreatingLoader({ isCreating: false }),
                MonitoringActions.createGrantedOrganizationsSuccess({
                  organization: response,
                }),
              ];
            }),
            catchError(() => {
              return [
                MonitoringActions.setIsCreatingLoader({ isCreating: false }),
              ];
            }),
          );
      }),
      share(),
    ),
  );

  public deleteGrantedOrganizations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.deleteGrantedOrganization),
      switchMap(({ orgId }) => {
        return [MonitoringActions.deleteGrantedOrganizationsRequest({ orgId })];
      }),
    ),
  );

  public deleteGrantedOrganizationsRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.deleteGrantedOrganizationsRequest),
      withLatestFrom(this.store$.select(MonitoringSelectors.selectOneAccount)),
      switchMap(([{ orgId }, account]) => {
        return this.monitoringService
          .deleteGrantedOrganization(account._id, orgId)
          .pipe(
            switchMap(() => {
              return [
                MonitoringActions.deleteGrantedOrganizationsSuccess({ orgId }),
              ];
            }),
            catchError(() => {
              return [];
            }),
          );
      }),
      share(),
    ),
  );

  private toAlertsV2ValidatorFormValue(val: unknown): unknown {
    if (!val || typeof val !== 'object') return val;
    const v = val as any;

    const camera: unknown = v?.camera;
    if (Array.isArray(camera)) {
      return val;
    }

    const cameras: unknown = v?.cameras;
    let normalizedCameraArr: unknown[];

    if (Array.isArray(cameras)) {
      normalizedCameraArr = cameras.length ? cameras : [null];
    } else if (camera != null) {
      normalizedCameraArr = [camera];
    } else {
      normalizedCameraArr = [null];
    }

    return { ...v, camera: normalizedCameraArr };
  }

  public policyFormValid$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        MonitoringActions.setPolicyFlow,
        MonitoringActions.setPolicyActions,
      ),
      withLatestFrom(
        this.store$.pipe(select(MonitoringSelectors.selectPolicyFlow)),
        this.store$.pipe(select(MonitoringSelectors.selectPolicyActions)),
        this.store$.pipe(select(MonitoringSelectors.selectPolicyFormValid)),
      ),
      switchMap(([, policyFlow, policyActions, currentValid]) => {
        if (!policyFlow) {
          return [MonitoringActions.setPolicyFormValid({ valid: false })];
        }

        let flowValid = true;
        let actionsValid = true;
        const actionsList = policyActions ?? [];

        const flow: AlertV2SelectedFlow = {
          category: policyFlow.category,
          flowType: policyFlow.flowType,
          formValue: this.toAlertsV2ValidatorFormValue(policyFlow.formValue) as any,
        };

        switch (flow.category) {
          case AlertCategory.Tracking:
            flowValid = this.alertsV2Effect['validateTracking'](flow);
            break;
          case AlertCategory.Safety:
            flowValid = this.alertsV2Effect['validateSafety'](flow);
            break;
          case AlertCategory.Identification:
            flowValid = this.alertsV2Effect['validateIdentification'](flow);
            break;
          case AlertCategory.Status:
            flowValid = this.alertsV2Effect['validateStatus'](flow);
            break;
          case AlertCategory.Integrations:
            flowValid = this.alertsV2Effect['validateIntegrations'](flow);
            break;
          case AlertCategory.CustomizedCapabilities:
            flowValid = this.alertsV2Effect['validateCustomizedCapabilities'](flow);
            break;
          case AlertCategory.ObjectStates:
            flowValid = this.alertsV2Effect['validateObjectStates'](flow);
            break;
        }

        for (const action of actionsList) {
          if (action.actionType !== null) {
            switch (+action?.actionType) {
              case ActionType.NOTIFY:
                if (
                  !action?.formValue?.notifications?.orgUsers?.length &&
                  !action?.formValue?.notifications?.manualUsers?.length
                ) {
                  actionsValid = false;
                }
                break;
              case ActionType.GPIO:
                if (
                  !action?.formValue['gpio'] ||
                  action?.formValue['gpio'] === 0
                ) {
                  actionsValid = false;
                }
                if (
                  !action?.formValue['duration'] ||
                  action?.formValue['duration'] === 0
                ) {
                  actionsValid = false;
                }
                if (
                  !action?.formValue['level'] ||
                  action?.formValue['level'] === 0
                ) {
                  actionsValid = false;
                }
                break;
              case ActionType.SEND_MESSAGE:
                if (
                  !!action?.formValue &&
                  !action?.formValue['httpRequest']['address']
                ) {
                  actionsValid = false;
                }
                break;
              case ActionType.INTEGRATION_PROVIDER_ACTION:
                const provider: IntegrationsModel.Providers =
                  action?.formValue?.provider;
                switch (provider) {
                  case IntegrationsModel.Providers.Slack:
                    if (!action.formValue?.params?.channel) {
                      actionsValid = false;
                    }
                    break;
                  case IntegrationsModel.Providers.HttpRequest:
                    const formValue =
                      action?.formValue as EventModels.Action.HttpRequestProviderActionFormValue;
                    if (!formValue?.params?.webhookId) {
                      actionsValid = false;
                    }
                    break;
                  case IntegrationsModel.Providers.Variables: {
                    const variablesFormValue =
                      action?.formValue as EventModels.Action.VariablesProviderActionFormValue;
                    const params = variablesFormValue?.params;
                    if (!params || !params.value) {
                      actionsValid = false;
                    }
                    break;
                  }
                  case IntegrationsModel.Providers.Teams: {
                    const teamsFormValue =
                      action?.formValue as EventModels.Action.MicrosoftTeamsProviderActionFormValue;
                    const params = teamsFormValue?.params;
                    if (
                      !params ||
                      !params.orgId ||
                      !params.channelId ||
                      !params.text
                    ) {
                      actionsValid = false;
                    }
                    break;
                  }
                }
                break;
              case ActionType.ARM_DISARM: {
                const formValue =
                  action?.formValue as EventModels.Action.AlertGroupChangeStateActionFormValue;
                if (
                  !formValue?.alertGroupId ||
                  (formValue?.state as any) == ''
                ) {
                  actionsValid = false;
                }
                break;
              }
              case ActionType.SYSLOG: {
                const formValue =
                  action?.formValue as EventModels.Action.SendSysLogRecordActionFormValue;
                if (!formValue?.address || !formValue?.protocol) {
                  actionsValid = false;
                }
                break;
              }
              case ActionType.EVENT_ENABLE_TOGGLE: {
                const formValue =
                  action?.formValue as EventModels.Action.EventEnableToggleActionFormValue;
                if (
                  !formValue?.eventId ||
                  (formValue?.enable as any) == ''
                ) {
                  actionsValid = false;
                }
                break;
              }
            }
          }
        }

        if (
          (flow.category === AlertCategory.Status &&
            flow.flowType === StatusType.Edge) ||
          (flow.category === AlertCategory.Integrations &&
            flow.flowType === IntegrationsType.EventTagTailGating)
        ) {
        } else {
          const hasZoneRegionIds =
            Array.isArray((flow?.formValue as any)?.zoneRegionsIds) &&
            !!(flow?.formValue as any)?.zoneRegionsIds?.length;
          if (
            flow?.formValue &&
            'camera' in flow?.formValue &&
            !hasZoneRegionIds &&
            !flow.formValue?.camera?.[0]?.cameraId
          ) {
            flowValid = false;
          }
        }

        const newValid = flowValid && actionsValid;
        if (currentValid !== newValid) {
          return [MonitoringActions.setPolicyFormValid({ valid: newValid })];
        }
        return [SharedActions.doNothing()];
      }),
    ),
  );

  public startCreatePolicy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.startCreatePolicy),
      switchMap(({ policyData }) => {
        return [
          MonitoringActions.setIsCreatingLoader({ isCreating: true }),
          MonitoringActions.startCreatePolicyRequest({ policyData }),
        ];
      }),
    ),
  );

  public startCreatePolicyRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.startCreatePolicyRequest),
      withLatestFrom(
        this.store$.select(MonitoringSelectors.selectOneAccount),
        this.store$.select(MonitoringSelectors.selectPartition),
      ),
      switchMap(([{ policyData }, account, partition]) => {
        return this.monitoringService
          .createPolicy(policyData, account._id, partition._id)
          .pipe(
            switchMap((response) => {
              return [
                SharedActions.showMessage({
                  success: this.translateService.instant(
                    'TOASTS.ENTITY_CREATED_SUCCESS',
                    {
                      entity: this.translateService.instant(
                        'MONITORING_PAGE.POLICIES.POLICY_LABEL',
                      ),
                    },
                  ),
                }),
                MonitoringActions.setIsCreatingLoader({ isCreating: false }),
                MonitoringActions.startCreatePolicySuccess({
                  policy: response,
                }),
              ];
            }),
            catchError(() => {
              return [
                SharedActions.showMessage({
                  error: this.translateService.instant(
                    'TOASTS.ENTITY_CREATED_FAIL',
                    {
                      entity: this.translateService.instant(
                        'MONITORING_PAGE.POLICIES.POLICY_LABEL',
                      ),
                    },
                  ),
                }),
                MonitoringActions.setIsCreatingLoader({ isCreating: false }),
              ];
            }),
          );
      }),
      share(),
    ),
  );

  public getPartitionPolicies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.getPartitionPolicies),
      switchMap(() => {
        return [
          MonitoringActions.setIsLoading({ isLoading: true }),
          MonitoringActions.getPartitionPoliciesRequest(),
        ];
      }),
    ),
  );

  public getPartitionPoliciesRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.getPartitionPoliciesRequest),
      withLatestFrom(this.store$.select(MonitoringSelectors.selectPartition)),
      switchMap(([, partition]) => {
        return this.monitoringService.getPolicies(partition?._id).pipe(
          switchMap((response) => {
            return [
              MonitoringActions.setIsLoading({ isLoading: false }),
              MonitoringActions.setPartitionPolicies({ policies: response }),
            ];
          }),
          catchError(() => {
            return [MonitoringActions.setIsLoading({ isLoading: false })];
          }),
        );
      }),
      share(),
    ),
  );

  public startUpdatePolicy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.startUpdatePolicy),
      switchMap(({ policyId, policyData }) => {
        return [
          MonitoringActions.setIsCreatingLoader({ isCreating: true }),
          MonitoringActions.startUpdatePolicyRequest({ policyId, policyData }),
        ];
      }),
    ),
  );

  public startUpdatePolicyRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.startUpdatePolicyRequest),
      switchMap(({ policyId, policyData }) => {
        return this.monitoringService.updatePolicy(policyId, policyData).pipe(
          switchMap(() => {
            return [
              SharedActions.showMessage({
                success: this.translateService.instant(
                  'TOASTS.ENTITY_UPDATE_SUCCESS',
                  {
                    entity: this.translateService.instant(
                      'MONITORING_PAGE.POLICIES.POLICY_LABEL',
                    ),
                  },
                ),
              }),
              MonitoringActions.setIsCreatingLoader({ isCreating: false }),
              MonitoringActions.startUpdatePolicySuccess({
                policyId,
                policyData,
              }),
            ];
          }),
          catchError(() => {
            return [
              SharedActions.showMessage({
                error: this.translateService.instant(
                  'TOASTS.ENTITY_UPDATE_FAILED',
                  {
                    entity: this.translateService.instant(
                      'MONITORING_PAGE.POLICIES.POLICY_LABEL',
                    ),
                  },
                ),
              }),
              MonitoringActions.setIsCreatingLoader({ isCreating: false }),
            ];
          }),
        );
      }),
      share(),
    ),
  );

  public startDeletePolicy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.startDeletePolicy),
      switchMap(({ policyId, policyName }) => {
        return this.monitoringService.deletePolicy(policyId).pipe(
          switchMap(() => {
            return [
              SharedActions.showMessage({
                info: this.translateService.instant(
                  'TOASTS.POLICY_HAS_BEEN_REMOVED',
                  { name: policyName },
                ),
              }),
              MonitoringActions.startDeletePolicySuccess({ policyId }),
            ];
          }),
          catchError(() => {
            return [
              SharedActions.showMessage({
                error: this.translateService.instant(
                  'TOASTS.ENTITY_HAS_BEEN_REMOVED_FAILED',
                  {
                    entity: this.translateService.instant(
                      'MONITORING_PAGE.POLICIES.POLICY_LABEL',
                    ),
                  },
                ),
              }),
            ];
          }),
        );
      }),
      share(),
    ),
  );

  public enablePolicy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.enablePolicy),
      switchMap(({ policyId }) => {
        return this.monitoringService.enablePolicy(policyId).pipe(
          switchMap(() => {
            return [MonitoringActions.setEnablePolicy({ policyId })];
          }),
          catchError(() => {
            return [
              SharedActions.showMessage({
                error: this.translateService.instant(
                  'TOASTS.ENTITY_UPDATE_FAILED',
                  {
                    entity: this.translateService.instant(
                      'MONITORING_PAGE.POLICIES.POLICY_LABEL',
                    ),
                  },
                ),
              }),
            ];
          }),
        );
      }),
      share(),
    ),
  );

  public disablePolicy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.disablePolicy),
      switchMap(({ policyId }) => {
        return this.monitoringService.disablePolicy(policyId).pipe(
          switchMap(() => {
            return [MonitoringActions.setDisablePolicy({ policyId })];
          }),
          catchError(() => {
            return [
              SharedActions.showMessage({
                error: this.translateService.instant(
                  'TOASTS.ENTITY_UPDATE_FAILED',
                  {
                    entity: this.translateService.instant(
                      'MONITORING_PAGE.POLICIES.POLICY_LABEL',
                    ),
                  },
                ),
              }),
            ];
          }),
        );
      }),
      share(),
    ),
  );

  public startDuplicatePolicy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitoringActions.startDuplicatePolicy),
      switchMap(({ policyId }) => {
        return this.monitoringService.duplicatePolicy(policyId).pipe(
          switchMap((response) => {
            return [
              SharedActions.showMessage({
                success: this.translateService.instant(
                  'TOASTS.ENTITY_CREATED_SUCCESS',
                  {
                    entity: this.translateService.instant(
                      'MONITORING_PAGE.POLICIES.POLICY_LABEL',
                    ),
                  },
                ),
              }),
              MonitoringActions.startDuplicatePolicySuccess({
                policy: response,
              }),
            ];
          }),
          catchError(() => {
            return [
              SharedActions.showMessage({
                error: this.translateService.instant(
                  'TOASTS.ENTITY_CREATED_FAIL',
                  {
                    entity: this.translateService.instant(
                      'MONITORING_PAGE.POLICIES.POLICY_LABEL',
                    ),
                  },
                ),
              }),
            ];
          }),
        );
      }),
      share(),
    ),
  );

  constructor(
    private store$: Store,
    private monitoringService: MonitoringService,
    private actions$: Actions,
    private translateService: TranslateService,
    private alertsV2Effect: AlertsV2Effect,
    private utilsService: UtilsService,
  ) {}
}
